import { Alert, Box, Button, CircularProgress, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemText, ListItemIcon } from '@mui/material'
import { useState } from 'react';
import { IDataDetalharPedido, IStatusProcessamentoReceita, IVerificarReceitaResponse } from '../../../../../../../types/api/comercial/detalhar-pedido';
import moment from 'moment';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DialogVisualizarReceituario from './DialogVisualizarReceituario';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosClient from '../../../../../../../lib/axios';
import { AxiosResponse } from 'axios';

export default function AlertReceituarioAgronomico({ data }: { data: IDataDetalharPedido }) {

    const queryClient = useQueryClient();

    const [openVisualizarReceita, setOpenVisualizarReceita] = useState(false);
    const [stop, setStop] = useState<boolean>(false);

    // Verifica se há produtos que precisam de receituário (emite_receita = "S")
    const itensComReceituario = data.itens.filter(item => item.emite_receita === "S");

    // Querys e mutations
    const { data: receitaData, isLoading, error } = useQuery<AxiosResponse<IVerificarReceitaResponse>>({
        queryKey: ["buscar-receita", data.id],
        queryFn: () =>
            axiosClient.get(`/receituario-agronomico/verificar-receita-emitida/${data.id}`),
        retry: false,
        cacheTime: 0,
        refetchInterval: stop ? false : 10000,
        onSuccess({ data: responseData }) {
            const todosProcessados = responseData.status_processamento.every(
                item => item.status === 'sucesso' || item.status === 'erro'
            );

            if (todosProcessados) {
                setStop(true);
                queryClient.invalidateQueries(`comercial/detalhar-pedido`);
            }
        },
        enabled: itensComReceituario.length > 0
    });

    const { mutate: reprocessar, isLoading: isLoadingReprocessar } = useMutation({
        mutationKey: "reprocessar-receita",
        mutationFn: (item: IStatusProcessamentoReceita) => {
            return axiosClient.post(`/receituario-agronomico/reprocessar-receita/${item.item_id}`);
        },
        onSuccess: () => {
            setStop(false);
            queryClient.invalidateQueries(`comercial/detalhar-pedido`);
            queryClient.invalidateQueries(["buscar-receita", data.id]);
        }
    });

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'sucesso':
                return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
            case 'erro':
                return <ErrorIcon sx={{ color: '#f44336' }} />;
            case 'processando':
                return <CircularProgress size={20} />;
            default:
                return <HourglassEmptyIcon sx={{ color: '#ff9800' }} />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'sucesso':
                return '#4caf50';
            case 'erro':
                return '#f44336';
            case 'processando':
                return '#2196f3';
            default:
                return '#ff9800';
        }
    };

    if (itensComReceituario.length === 0) {
        return null;
    }

    const statusProcessamento = receitaData?.data.status_processamento || [];
    const temSucesso = statusProcessamento.some(item => item.status === 'sucesso');
    const temErro = statusProcessamento.some(item => item.status === 'erro');
    const temProcessando = statusProcessamento.some(item => item.status === 'processando' || item.status === 'nao_iniciado');


    const handleRetry = (item: IStatusProcessamentoReceita) => {
        reprocessar(item);
    }

    const getAccordionStatus = () => {
        if (error) {
            return {
                icon: <ErrorIcon sx={{ color: '#f44336' }} />,
                text: 'Erro ao verificar status',
                color: '#f44336'
            };
        }
        if (isLoading || isLoadingReprocessar) {
            return {
                icon: <CircularProgress size={20} />,
                text: 'Verificando status...',
                color: '#2196f3'
            };
        }
        if (temProcessando) {
            return {
                icon: <CircularProgress size={20} />,
                text: 'Em processamento',
                color: '#2196f3'
            };
        }
        if (temSucesso && !temErro) {
            return {
                icon: <CheckCircleIcon sx={{ color: '#4caf50' }} />,
                text: 'Concluído com sucesso',
                color: '#4caf50'
            };
        }
        if (temErro) {
            return {
                icon: <ErrorIcon sx={{ color: '#f44336' }} />,
                text: 'Concluído com erros',
                color: '#f44336'
            };
        }
        return {
            icon: <HourglassEmptyIcon sx={{ color: '#ff9800' }} />,
            text: 'Aguardando processamento',
            color: '#ff9800'
        };
    };

    const accordionStatus = getAccordionStatus();

    return (
        <>
            <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                        {accordionStatus.icon}
                        <Box>
                            {
                                receitaData?.data.quantidade_receitas_mensagem &&
                                <Typography variant="caption" color="error" display="block">
                                    {receitaData?.data.quantidade_receitas_mensagem}
                                </Typography>
                            }
                            <Typography variant="subtitle2" sx={{ color: accordionStatus.color }}>
                                Receituário Agronômico - {accordionStatus.text}
                            </Typography>
                            {statusProcessamento.length > 0 && (
                                <Typography variant="caption" color="textSecondary">
                                    {statusProcessamento.length} {statusProcessamento.length === 1 ? 'produto' : 'produtos'}
                                </Typography>
                            )}
                        </Box>
                        {temSucesso && !temProcessando && !error && (
                            <Box ml="auto">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VisibilityOutlinedIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenVisualizarReceita(true);
                                    }}
                                >
                                    Visualizar
                                </Button>
                            </Box>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {error ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                Erro ao verificar status do receituário agronômico. Tentando novamente automaticamente...
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Informações do pedido:</strong>
                            </Typography>
                            <List dense>
                                {itensComReceituario.map((item) => (
                                    <ListItem key={item.id} sx={{ px: 0 }}>
                                        <ListItemIcon>
                                            <HourglassEmptyIcon sx={{ color: '#ff9800' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {item.nome}
                                                    </Typography>
                                                    <Chip
                                                        label="Aguardando verificação"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#ff9800',
                                                            color: 'white',
                                                            fontWeight: 'bold'
                                                        }}
                                                    />
                                                </Box>
                                            }
                                            secondary="Status será atualizado quando a conexão for reestabelecida"
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Alert>
                    ) : (isLoading || isLoadingReprocessar) ? (
                        <Box display="flex" alignItems="center" gap={2} p={2}>
                            <CircularProgress size={24} />
                            <Typography>Verificando status do receituário agronômico...</Typography>
                        </Box>
                    ) : statusProcessamento.length > 0 ? (
                        <>
                            {temSucesso && !temProcessando && data.receita_agronomo && data.receita_data_emissao && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Receituário agronômico emitido com sucesso por {data.receita_agronomo} no dia {moment(data.receita_data_emissao).format("DD/MM/YYYY")}
                                </Alert>
                            )}

                            {temProcessando && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Receituário agronômico em processamento. Isso pode demorar alguns minutos.
                                </Alert>
                            )}

                            {temErro && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    Alguns itens apresentaram erro no processamento do receituário.
                                </Alert>
                            )}

                            <List dense>
                                {statusProcessamento.map((item) => (
                                    <ListItem key={item.item_id} sx={{ px: 0 }}>
                                        <ListItemIcon>
                                            {getStatusIcon(item.status)}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {item.produto_nome}
                                                    </Typography>
                                                    <Chip
                                                        label={item.status_formatado}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: getStatusColor(item.status),
                                                            color: 'white',
                                                            fontWeight: 'bold'
                                                        }}
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    {item.processado_em && (
                                                        <Typography variant="caption" color="textSecondary">
                                                            Processado em: {item.processado_em}
                                                        </Typography>
                                                    )}
                                                    {item.erro_detalhes && (
                                                        <>
                                                            <Typography variant="caption" color="error" display="block">
                                                                Erro: {item.erro_detalhes}
                                                            </Typography>
                                                            <Button variant="text" color="primary" onClick={() => handleRetry(item)}>
                                                                Tentar Novamente
                                                            </Button>
                                                        </>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    ) : (
                        <Typography color="textSecondary">
                            Nenhum dado de processamento disponível no momento.
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>

            {openVisualizarReceita && (
                <DialogVisualizarReceituario
                    {...{ data }}
                    open={openVisualizarReceita}
                    handleClose={() => setOpenVisualizarReceita(false)}
                />
            )}
        </>
    )
}
